import React from "react";
import PropTypes from "prop-types";
import { gql, graphql } from "react-apollo";
import {
  CardText,
  CircularProgress,
  Paper,
  Card,
  CardHeader,
} from "material-ui";
import moment from "moment";
import { StyleSheet, css } from "aphrodite";
import {
  VictoryArea,
  VictoryChart,
  VictoryAxis,
  VictoryStack,
  VictoryLegend,
  VictoryPie,
} from "victory";
import OurismanTravelTheme from "../../../ourismanTravelTheme";

const AdminReportQuery = gql`
  query AdminReport($startDate: String!, $endDate: String!) {
    adminReport(startDate: $startDate, endDate: $endDate) {
      dormant
      pending
      booked
      voided
      numCreated
      numVoided
      numBooked
      numCancelled
      numSegmentsBooked
      numSegmentsCancelled
      numDrafted
      numInvoiced
      numSegments
      timeToBook
      timeToDropbox
      timeToInvoice
      icDormant
      icPending
      icBooked
      icVoided
      icNumCreated
      icNumVoided
      icNumBooked
      icNumCancelled
      icNumSegmentsBooked
      icNumSegmentsCancelled
      icNumDrafted
      icNumInvoiced
      icNumSegments
      icTimeToBook
      icTimeToDropbox
      icTimeToInvoice
      otherDormant
      otherPending
      otherBooked
      otherVoided
      otherNumCreated
      otherNumVoided
      otherNumBooked
      otherNumCancelled
      otherNumSegmentsBooked
      otherNumSegmentsCancelled
      otherNumDrafted
      otherNumInvoiced
      otherNumSegments
      otherTimeToBook
      otherTimeToDropbox
      otherTimeToInvoice
      avgTripsCreated
      avgTripsBooked
      icAvgTripsCreated
      icAvgTripsBooked
      otherAvgTripsCreated
      otherAvgTripsBooked
      tripCounts
    }
  }
`;

@graphql(AdminReportQuery)
class AdminReport extends React.Component {
  static propTypes = {
    data: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      allResponses: PropTypes.shape({
        dormant: PropTypes.number.isRequired,
        modified: PropTypes.arrayOf(PropTypes.shape()).isRequired,
      }),
    }).isRequired,
    dateString: PropTypes.string.isRequired,
  };

  render() {
    const { data, dateString } = this.props;

    if (data.loading) {
      return (
        <CardText className={css(styles.report)}>
          <CircularProgress className={css(styles.progressIndicator)} />
        </CardText>
      );
    }

    const {
      adminReport: {
        dormant,
        pending,
        booked,
        voided,
        numCreated,
        numVoided,
        numBooked,
        numCancelled,
        numSegmentsBooked,
        numSegmentsCancelled,
        numDrafted,
        numInvoiced,
        numSegments,
        timeToBook,
        timeToDropbox,
        timeToInvoice,
        icDormant,
        icPending,
        icBooked,
        icVoided,
        icNumCreated,
        icNumVoided,
        icNumBooked,
        icNumCancelled,
        icNumSegmentsBooked,
        icNumSegmentsCancelled,
        icNumDrafted,
        icNumInvoiced,
        icNumSegments,
        icTimeToBook,
        icTimeToDropbox,
        icTimeToInvoice,
        otherDormant,
        otherPending,
        otherBooked,
        otherVoided,
        otherNumCreated,
        otherNumVoided,
        otherNumBooked,
        otherNumCancelled,
        otherNumSegmentsBooked,
        otherNumSegmentsCancelled,
        otherNumDrafted,
        otherNumInvoiced,
        otherNumSegments,
        otherTimeToBook,
        otherTimeToDropbox,
        otherTimeToInvoice,
        avgTripsCreated,
        avgTripsBooked,
        icAvgTripsCreated,
        icAvgTripsBooked,
        otherAvgTripsCreated,
        otherAvgTripsBooked,
        tripCounts,
      },
    } = data;

    const formattedDuration = (seconds) => {
      if (seconds === 0) {
        return "n/a";
      }
      const momentDuration = moment.duration(seconds, "seconds");
      const hours = momentDuration.as("hours").toFixed(0);

      if (hours < 1) {
        return momentDuration.humanize();
      }

      return `${hours} hours`;
    };

    const bookDuration = formattedDuration(timeToBook);
    const dropboxDuration = formattedDuration(timeToDropbox);
    const invoiceDuration = formattedDuration(timeToInvoice);

    const icBookDuration = formattedDuration(icTimeToBook);
    const icDropboxDuration = formattedDuration(icTimeToDropbox);
    const icInvoiceDuration = formattedDuration(icTimeToInvoice);

    const otherBookDuration = formattedDuration(otherTimeToBook);
    const otherDropboxDuration = formattedDuration(otherTimeToDropbox);
    const otherInvoiceDuration = formattedDuration(otherTimeToInvoice);

    const tripCountRaw = JSON.parse(tripCounts);

    const icTripsData = Object.entries(
      tripCountRaw.insideCircle
    ).map(([k, v]) => ({ month: k, count: v }));
    const otherTripsData = Object.entries(tripCountRaw.other).map(([k, v]) => ({
      month: k,
      count: v,
    }));

    const axisStyles = {
      tickLabels: {
        fontSize: 5,
      },
    };
    const lineStyles = {
      labels: {
        fontSize: 5,
      },
    };

    return (
      <div>
        <Card className={css(styles.report)}>
          <CardHeader title="All Time Averages" />
          <CardText>
            <div className={css(styles.summaryContainer)}>
              <Paper className={css(styles.summaryBox)}>
                <span className={css(styles.boxHeader)}>All Trips</span>
                <div className={css(styles.summaryRow)}>
                  <span>Average trips created per month:</span>
                  <span>{avgTripsCreated}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Average trips booked per month:</span>
                  <span>{avgTripsBooked}</span>
                </div>
              </Paper>
              <Paper className={css(styles.summaryBox)}>
                <span className={css(styles.boxHeader)}>
                  Inside Circle Trips
                </span>
                <div className={css(styles.summaryRow)}>
                  <span>Average trips created per month:</span>
                  <span>{icAvgTripsCreated}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Average trips booked per month:</span>
                  <span>{icAvgTripsBooked}</span>
                </div>
              </Paper>
              <Paper className={css(styles.summaryBox)}>
                <span className={css(styles.boxHeader)}>Other Trips</span>
                <div className={css(styles.summaryRow)}>
                  <span>Average trips created per month:</span>
                  <span>{otherAvgTripsCreated}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Average trips booked per month:</span>
                  <span>{otherAvgTripsBooked}</span>
                </div>
              </Paper>
            </div>
            <div className={css(styles.summaryContainer)}>
              <VictoryChart domainPadding={20} height={200}>
                <VictoryLegend
                  x={75}
                  y={50}
                  orientation="horizontal"
                  data={[
                    {
                      name: "Inside Circle",
                      symbol: {
                        fill: OurismanTravelTheme.palette.insideCircle,
                      },
                    },
                    {
                      name: "Other",
                      symbol: {
                        fill: OurismanTravelTheme.palette.accent2Color,
                      },
                    },
                  ]}
                  style={{ labels: { fontSize: 7 } }}
                />
                <VictoryAxis
                  style={axisStyles}
                  tickValues={Object.keys(tripCountRaw.all)}
                />
                <VictoryAxis dependentAxis style={axisStyles} />
                <VictoryStack colorScale="qualitative" offset={6}>
                  <VictoryArea
                    data={icTripsData}
                    x="month"
                    y="count"
                    labels={(datum) => datum.y}
                    style={{
                      ...lineStyles,
                      data: {
                        fill: OurismanTravelTheme.palette.insideCircle,
                      },
                    }}
                  />
                  <VictoryArea
                    data={otherTripsData}
                    x="month"
                    y="count"
                    labels={(datum) => datum.y}
                    style={{
                      ...lineStyles,
                      data: {
                        fill: OurismanTravelTheme.palette.accent2Color,
                      },
                    }}
                  />
                </VictoryStack>
              </VictoryChart>
            </div>
          </CardText>
        </Card>
        <Card className={css(styles.report)}>
          <CardHeader title="All Trips" />
          <CardText>
            <div className={css(styles.summaryContainer)}>
              <Paper className={css(styles.summaryBoxWithChart)}>
                <div className={css(styles.summaryBoxData)}>
                  <span className={css(styles.boxHeader)}>
                    General trip information
                  </span>
                  <div className={css(styles.summaryRow)}>
                    <span>Trips dormant for seven or more days:</span>
                    <span>{dormant}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Trips pending:</span>
                    <span>{pending}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Trips booked:</span>
                    <span>{booked}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Trips voided:</span>
                    <span>{voided}</span>
                  </div>
                </div>
                <div className={css(styles.summaryBoxChart)}>
                  <VictoryPie
                    colorScale="warm"
                    labelRadius={70}
                    style={{
                      labels: {
                        fill: "white",
                        fontSize: 15,
                        fontWeight: "bold",
                      },
                    }}
                    data={[
                      { x: "Pending", y: pending },
                      { x: "Booked", y: booked },
                      { x: "Voided", y: voided },
                    ]}
                  />
                </div>
              </Paper>
              <Paper className={css(styles.summaryBox)}>
                <span className={css(styles.boxHeader)}>
                  Average times during {dateString}
                </span>
                <div className={css(styles.summaryRow)}>
                  <span>Trip created to all segments booked:</span>
                  <span>{bookDuration}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Segment booked to filed:</span>
                  <span>{dropboxDuration}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Segment booked to invoiced:</span>
                  <span>{invoiceDuration}</span>
                </div>
              </Paper>
              <Paper className={css(styles.summaryBox)}>
                <span className={css(styles.boxHeader)}>
                  Trips acted upon during {dateString}
                </span>
                <div className={css(styles.summaryRow)}>
                  <span>Trips created:</span>
                  <span>{numCreated}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Trips voided:</span>
                  <span>{numVoided}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Trips with segments booked:</span>
                  <span>{numBooked}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Trips with segments cancelled:</span>
                  <span>{numCancelled}</span>
                </div>
              </Paper>
              <Paper className={css(styles.summaryBoxWithChart)}>
                <div className={css(styles.summaryBoxData)}>
                  <span className={css(styles.boxHeader)}>
                    Segments acted upon during {dateString}
                  </span>
                  <div className={css(styles.summaryRow)}>
                    <span>Total:</span>
                    <span>{numSegments}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Segments booked:</span>
                    <span>{numSegmentsBooked}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Segments cancelled:</span>
                    <span>{numSegmentsCancelled}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Segments with VIP drafted:</span>
                    <span>{numDrafted}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Segments invoiced:</span>
                    <span>{numInvoiced}</span>
                  </div>
                </div>
                <div className={css(styles.summaryBoxChart)}>
                  <VictoryPie
                    colorScale="warm"
                    labelRadius={70}
                    style={{
                      labels: {
                        fill: "white",
                        fontSize: 15,
                        fontWeight: "bold",
                      },
                    }}
                    data={[
                      { x: "Booked", y: numSegmentsBooked },
                      { x: "Cancelled", y: numSegmentsCancelled },
                    ]}
                  />
                </div>
              </Paper>
            </div>
          </CardText>
        </Card>
        <Card className={css(styles.report)}>
          <CardHeader title="Inside Circle Trips" />
          <CardText>
            <div className={css(styles.summaryContainer)}>
              <Paper className={css(styles.summaryBoxWithChart)}>
                <div className={css(styles.summaryBoxData)}>
                  <span className={css(styles.boxHeader)}>
                    General trip information
                  </span>
                  <div className={css(styles.summaryRow)}>
                    <span>Trips dormant for seven or more days:</span>
                    <span>{icDormant}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Trips pending:</span>
                    <span>{icPending}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Trips booked:</span>
                    <span>{icBooked}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Trips voided:</span>
                    <span>{icVoided}</span>
                  </div>
                </div>
                <div className={css(styles.summaryBoxChart)}>
                  <VictoryPie
                    colorScale="warm"
                    labelRadius={70}
                    style={{
                      labels: {
                        fill: "white",
                        fontSize: 15,
                        fontWeight: "bold",
                      },
                    }}
                    data={[
                      { x: "Pending", y: icPending },
                      { x: "Booked", y: icBooked },
                      { x: "Voided", y: icVoided },
                    ]}
                  />
                </div>
              </Paper>
              <Paper className={css(styles.summaryBox)}>
                <span className={css(styles.boxHeader)}>
                  Average times during {dateString}
                </span>
                <div className={css(styles.summaryRow)}>
                  <span>Trip created to all segments booked:</span>
                  <span>{icBookDuration}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Segment booked to filed:</span>
                  <span>{icDropboxDuration}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Segment booked to invoiced:</span>
                  <span>{icInvoiceDuration}</span>
                </div>
              </Paper>
              <Paper className={css(styles.summaryBox)}>
                <span className={css(styles.boxHeader)}>
                  Trips acted upon during {dateString}
                </span>
                <div className={css(styles.summaryRow)}>
                  <span>Trips created:</span>
                  <span>{icNumCreated}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Trips voided:</span>
                  <span>{icNumVoided}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Trips with segments booked:</span>
                  <span>{icNumBooked}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Trips with segments cancelled:</span>
                  <span>{icNumCancelled}</span>
                </div>
              </Paper>
              <Paper className={css(styles.summaryBoxWithChart)}>
                <div className={css(styles.summaryBoxData)}>
                  <span className={css(styles.boxHeader)}>
                    Segments acted upon during {dateString}
                  </span>
                  <div className={css(styles.summaryRow)}>
                    <span>Total:</span>
                    <span>{icNumSegments}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Segments booked:</span>
                    <span>{icNumSegmentsBooked}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Segments cancelled:</span>
                    <span>{icNumSegmentsCancelled}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Segments with VIP drafted:</span>
                    <span>{icNumDrafted}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Segments invoiced:</span>
                    <span>{icNumInvoiced}</span>
                  </div>
                </div>
                <div className={css(styles.summaryBoxChart)}>
                  <VictoryPie
                    colorScale="warm"
                    labelRadius={70}
                    style={{
                      labels: {
                        fill: "white",
                        fontSize: 15,
                        fontWeight: "bold",
                      },
                    }}
                    data={[
                      { x: "Booked", y: icNumSegmentsBooked },
                      { x: "Cancelled", y: icNumSegmentsCancelled },
                    ]}
                  />
                </div>
              </Paper>
            </div>
          </CardText>
        </Card>
        <Card className={css(styles.report)}>
          <CardHeader title="Other Trips" />
          <CardText>
            <div className={css(styles.summaryContainer)}>
              <Paper className={css(styles.summaryBoxWithChart)}>
                <div className={css(styles.summaryBoxData)}>
                  <span className={css(styles.boxHeader)}>
                    General trip information
                  </span>
                  <div className={css(styles.summaryRow)}>
                    <span>Trips dormant for seven or more days:</span>
                    <span>{otherDormant}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Trips pending:</span>
                    <span>{otherPending}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Trips booked:</span>
                    <span>{otherBooked}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Trips voided:</span>
                    <span>{otherVoided}</span>
                  </div>
                </div>
                <div className={css(styles.summaryBoxChart)}>
                  <VictoryPie
                    colorScale="warm"
                    labelRadius={70}
                    style={{
                      labels: {
                        fill: "white",
                        fontSize: 15,
                        fontWeight: "bold",
                      },
                    }}
                    data={[
                      { x: "Pending", y: otherPending },
                      { x: "Booked", y: otherBooked },
                      { x: "Voided", y: otherVoided },
                    ]}
                  />
                </div>
              </Paper>
              <Paper className={css(styles.summaryBox)}>
                <span className={css(styles.boxHeader)}>
                  Average times during {dateString}
                </span>
                <div className={css(styles.summaryRow)}>
                  <span>Trip created to all segments booked:</span>
                  <span>{otherBookDuration}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Segment booked to filed:</span>
                  <span>{otherDropboxDuration}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Segment booked to invoiced:</span>
                  <span>{otherInvoiceDuration}</span>
                </div>
              </Paper>
              <Paper className={css(styles.summaryBox)}>
                <span className={css(styles.boxHeader)}>
                  Trips acted upon during {dateString}
                </span>
                <div className={css(styles.summaryRow)}>
                  <span>Trips created:</span>
                  <span>{otherNumCreated}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Trips voided:</span>
                  <span>{otherNumVoided}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Trips with segments booked:</span>
                  <span>{otherNumBooked}</span>
                </div>
                <div className={css(styles.summaryRow)}>
                  <span>Trips with segments cancelled:</span>
                  <span>{otherNumCancelled}</span>
                </div>
              </Paper>
              <Paper className={css(styles.summaryBoxWithChart)}>
                <div className={css(styles.summaryBoxData)}>
                  <span className={css(styles.boxHeader)}>
                    Segments acted upon during {dateString}
                  </span>
                  <div className={css(styles.summaryRow)}>
                    <span>Total:</span>
                    <span>{otherNumSegments}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Segments booked:</span>
                    <span>{otherNumSegmentsBooked}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Segments cancelled:</span>
                    <span>{otherNumSegmentsCancelled}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Segments with VIP drafted:</span>
                    <span>{otherNumDrafted}</span>
                  </div>
                  <div className={css(styles.summaryRow)}>
                    <span>Segments invoiced:</span>
                    <span>{otherNumInvoiced}</span>
                  </div>
                </div>
                <div className={css(styles.summaryBoxChart)}>
                  <VictoryPie
                    colorScale="warm"
                    labelRadius={70}
                    style={{
                      labels: {
                        fill: "white",
                        fontSize: 15,
                        fontWeight: "bold",
                      },
                    }}
                    data={[
                      { x: "Booked", y: otherNumSegmentsBooked },
                      { x: "Cancelled", y: otherNumSegmentsCancelled },
                    ]}
                  />
                </div>
              </Paper>
            </div>
          </CardText>
        </Card>
      </div>
    );
  }
}

const styles = StyleSheet.create({
  report: {
    margin: 15,
    minHeight: "100%",
  },
  progressIndicator: {
    position: "relative",
    marginLeft: "50%",
    left: -20,
    top: -20,
  },
  summaryContainer: {
    display: "flex",
    flexWrap: "wrap",
  },
  summaryBox: {
    display: "flex",
    flexDirection: "column",
    padding: 10,
    marginRight: 25,
    marginBottom: 25,
    width: 350,
  },
  summaryBoxWithChart: {
    display: "flex",
    flexWrap: "wrap",
    padding: 10,
    marginRight: 25,
    marginBottom: 25,
    width: 725,
  },
  summaryBoxData: {
    display: "flex",
    flexDirection: "column",
    width: 330,
    marginRight: 25,
    marginBottom: 25,
  },
  summaryBoxChart: {
    width: 330,
  },
  boxHeader: {
    fontWeight: "bold",
    lineHeight: "1.5em",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
  },
});

export default AdminReport;



// WEBPACK FOOTER //
// /src/components/Reports/Admin/AdminReport.js